import { categories } from "@/mocks/categories";
import { users } from "@/mocks/users";
import { topics } from "@/mocks/topics";

export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  topicCount: number;
}

export interface CommunityUser {
  id: string;
  name: string;
  avatar: string;
  bio: string;
  location: string;
  followers: number;
  following: number;
  joinDate: string;
  tags: string[];
}

export interface CommentReply {
  id: string;
  authorId: string;
  content: string;
  date: string;
  likes: number;
}

export interface Comment extends CommentReply {
  replies: CommentReply[];
}

export interface Topic {
  id: string;
  title: string;
  categoryId: string;
  authorId: string;
  date: string;
  type: string;
  excerpt: string;
  cover: string;
  tags: string[];
  views: number;
  replies: number;
  content: string[];
  comments: Comment[];
}

export const allCategories = categories as unknown as Category[];
export const allUsers = users as unknown as CommunityUser[];
export const allTopics = topics as unknown as Topic[];

export function getCategory(id: string): Category | undefined {
  return allCategories.find((c) => c.id === id);
}

export function getCategoryName(id: string): string {
  return getCategory(id)?.name ?? "综合话题";
}

export function getUser(id: string): CommunityUser | undefined {
  return allUsers.find((u) => u.id === id);
}

export function getUserName(id: string): string {
  return getUser(id)?.name ?? "匿名用户";
}

export function getUserAvatar(id: string): string {
  return getUser(id)?.avatar ?? "";
}

export function getTopic(id: string): Topic | undefined {
  return allTopics.find((t) => t.id === id);
}

export function getTopicsByCategory(categoryId: string): Topic[] {
  return allTopics.filter((t) => t.categoryId === categoryId);
}

export function getTopicsByUser(userId: string): Topic[] {
  return allTopics.filter((t) => t.authorId === userId);
}

export function getRelatedTopics(topic: Topic, limit = 4): Topic[] {
  return allTopics
    .filter((t) => t.id !== topic.id)
    .map((t) => {
      let score = 0;
      if (t.categoryId === topic.categoryId) score += 3;
      score += t.tags.filter((tag) => topic.tags.includes(tag)).length * 2;
      return { t, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.t);
}

export function searchTopics(keyword: string): Topic[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allTopics.filter(
    (t) =>
      t.title.toLowerCase().includes(kw) ||
      t.excerpt.toLowerCase().includes(kw) ||
      t.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
      t.content.some((p) => p.toLowerCase().includes(kw))
  );
}

export function getHottestTopics(limit = 5): Topic[] {
  return [...allTopics].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function formatDate(date: string): string {
  const d = new Date(date);
  const now = new Date("2025-10-12");
  const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "今天";
  if (diff === 1) return "昨天";
  if (diff < 7) return `${diff} 天前`;
  if (diff < 30) return `${Math.floor(diff / 7)} 周前`;
  return date;
}